/**
 * Optional per-session-type scheduling rules (frontend mirror of the backend
 * `scheduling_rules` JSON column). These constrain which slots are bookable on
 * top of the existing daily time window and date range. Absent rules mean no
 * constraints — existing booking behavior is preserved.
 *
 * Times/days are interpreted in the session type timezone, consistent with the
 * daily time window.
 */

export interface BreakHour {
  start: string; // 'HH:MM' (24h)
  end: string; // 'HH:MM' (24h)
  tag?: string;
  key?: string; // client-only stable React key; never sent to the backend
}

export interface BlockedDate {
  date: string; // 'YYYY-MM-DD'
  label?: string;
  key?: string; // client-only stable React key; never sent to the backend
}

export interface SchedulingRules {
  workingDays: number[]; // allowed weekdays 0=Sun..6=Sat; empty = all days
  breakHours: BreakHour[];
  blockedDates: BlockedDate[];
}

// Day-of-week labels, indexed 0=Sun..6=Sat to match JS Date.getDay().
export const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

// Preset break tags shown in the tag dropdown. "Other" allows free-text entry.
export const BREAK_TAG_PRESETS = [
  'Coffee Break',
  'Lunch',
  'Tea Break',
  'Stand-up',
] as const;

export const emptySchedulingRules = (): SchedulingRules => ({
  workingDays: [],
  breakHours: [],
  blockedDates: [],
});

// -------------------------------------------------------------------------
// Slot generation mode (OD-1002). Frontend mirror of the backend
// schedule_session_type.slot_generation_mode column.
// -------------------------------------------------------------------------
export type SlotGenerationMode = 'session_rules' | 'moderator_availability';

export const SESSION_RULES_MODE: SlotGenerationMode = 'session_rules';
export const MODERATOR_AVAILABILITY_MODE: SlotGenerationMode =
  'moderator_availability';
export const DEFAULT_SLOT_GENERATION_MODE: SlotGenerationMode = 'session_rules';

/** Info banners shown under the mode selector (verbatim from AC2 / AC3). */
export const SLOT_MODE_BANNERS: Record<SlotGenerationMode, string> = {
  session_rules:
    'Slots are generated based on Time Range, Working Days, Break Hours, and Blocked Dates configured below.',
  moderator_availability:
    "Slots are generated based only on the assigned moderators' availability. Slots will break according to each moderator's availability.",
};

/** Read the mode off a session type record; defaults to Session Rules (AC9). */
export const slotGenerationModeFromSessionType = (
  sessionType: unknown,
): SlotGenerationMode => {
  const raw = (sessionType as {slot_generation_mode?: unknown} | null | undefined)
    ?.slot_generation_mode;
  return raw === MODERATOR_AVAILABILITY_MODE
    ? MODERATOR_AVAILABILITY_MODE
    : SESSION_RULES_MODE;
};

/** True when the session type derives slots from moderator availability. */
export const isModeratorAvailabilityMode = (
  mode: SlotGenerationMode,
): boolean => mode === MODERATOR_AVAILABILITY_MODE;

interface RawSchedulingRules {
  working_days?: unknown;
  break_hours?: unknown;
  blocked_dates?: unknown;
}

const parseRawSchedulingRules = (raw: unknown): RawSchedulingRules | null => {
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as RawSchedulingRules;
    } catch {
      return null;
    }
  }
  if (raw && typeof raw === 'object') return raw as RawSchedulingRules;
  return null;
};

/** Stringify a primitive raw-rule field value; non-primitives fall back to ''. */
const safeString = (value: unknown): string =>
  typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
    ? String(value)
    : '';

const parseWorkingDays = (obj: RawSchedulingRules): number[] =>
  Array.isArray(obj.working_days)
    ? obj.working_days
        .map((d) => Number(d))
        .filter((d) => Number.isInteger(d) && d >= 0 && d <= 6)
    : [];

// Trim to match the backend, which trims before validating; keeps the two
// rule implementations in agreement on whitespace-padded times.
const parseBreakHours = (obj: RawSchedulingRules): BreakHour[] =>
  Array.isArray(obj.break_hours)
    ? (obj.break_hours as Array<Record<string, unknown>>)
        .filter((b) => b && typeof b === 'object')
        .map((b) => {
          const entry: BreakHour = {
            start: safeString(b.start).trim(),
            end: safeString(b.end).trim(),
          };
          const tag = typeof b.tag === 'string' ? b.tag.trim() : '';
          if (tag) entry.tag = tag;
          return entry;
        })
    : [];

const parseBlockedDates = (obj: RawSchedulingRules): BlockedDate[] =>
  Array.isArray(obj.blocked_dates)
    ? (obj.blocked_dates as Array<Record<string, unknown>>)
        .filter((bd) => bd && typeof bd === 'object')
        .map((bd) => {
          const entry: BlockedDate = {date: safeString(bd.date).trim()};
          const label = typeof bd.label === 'string' ? bd.label.trim() : '';
          if (label) entry.label = label;
          return entry;
        })
    : [];

/**
 * Read scheduling rules off a session type record (API returns the JSON column
 * as `scheduling_rules`). Defensive: tolerates null, a JSON string, or object.
 */
export const rulesFromSessionType = (sessionType: unknown): SchedulingRules => {
  const empty = emptySchedulingRules();
  const raw = (sessionType as {scheduling_rules?: unknown} | null | undefined)
    ?.scheduling_rules;
  if (!raw) return empty;

  const obj = parseRawSchedulingRules(raw);
  if (!obj) return empty;

  return {
    workingDays: parseWorkingDays(obj),
    breakHours: parseBreakHours(obj),
    blockedDates: parseBlockedDates(obj),
  };
};

/**
 * Serialize form rules into the API payload shape, or null when nothing
 * meaningful is set (so the backend stores NULL and preserves legacy behavior).
 * Drops incomplete break rows (missing start/end).
 */
export const rulesToPayload = (
  rules: SchedulingRules,
): {
  working_days?: number[];
  break_hours?: BreakHour[];
  blocked_dates?: BlockedDate[];
} | null => {
  const workingDays = Array.from(new Set(rules.workingDays))
    .filter((d) => Number.isInteger(d) && d >= 0 && d <= 6)
    .sort((a, b) => a - b);

  const breakHours = rules.breakHours
    .filter((b) => b.start && b.end)
    .map((b) => {
      const entry: BreakHour = {start: b.start, end: b.end};
      const tag = b.tag?.trim();
      if (tag) entry.tag = tag;
      return entry;
    });

  const blockedDates = rules.blockedDates
    .filter((bd) => bd.date)
    .map((bd) => {
      const entry: BlockedDate = {date: bd.date};
      const label = bd.label?.trim();
      if (label) entry.label = label;
      return entry;
    });

  if (workingDays.length === 0 && breakHours.length === 0 && blockedDates.length === 0) {
    return null;
  }

  const payload: {
    working_days?: number[];
    break_hours?: BreakHour[];
    blocked_dates?: BlockedDate[];
  } = {};
  if (workingDays.length > 0) payload.working_days = workingDays;
  if (breakHours.length > 0) payload.break_hours = breakHours;
  if (blockedDates.length > 0) payload.blocked_dates = blockedDates;
  return payload;
};

// ---------------------------------------------------------------------------
// Runtime evaluation (slot generation / calendar disabling). Kept in sync with
// the backend mirror in data-collector-be/src/services/schedulingRules.js.
// Inputs are in the session type timezone: day-of-week 0=Sun..6=Sat,
// minutes-of-day, and 'YYYY-MM-DD' dates.
// ---------------------------------------------------------------------------

export interface MinuteInterval {
  startMin: number;
  endMin: number;
  alignToMin?: number;
}

const HHMM = /^([01]\d|2[0-3]):([0-5]\d)$/;
const hhmmToMinutes = (s: string): number => {
  const [h, m] = s.split(':').map(Number);
  return h * 60 + m;
};

/** Whether a calendar day is bookable under the rules (working days + blocked dates). */
export const isDayAllowed = (
  rules: SchedulingRules,
  dayOfWeek: number,
  ymd: string,
): boolean => {
  if (rules.workingDays.length > 0 && !rules.workingDays.includes(dayOfWeek)) {
    return false;
  }
  if (rules.blockedDates.some((b) => b.date === ymd)) return false;
  return true;
};

/** Break ranges as minute-of-day intervals, or [] when none. */
export const getBreakIntervalsMinutes = (
  rules: SchedulingRules,
): MinuteInterval[] => {
  const intervals: MinuteInterval[] = [];
  for (const b of rules.breakHours) {
    if (!HHMM.test(b.start) || !HHMM.test(b.end)) continue;
    const startMin = hhmmToMinutes(b.start);
    const endMin = hhmmToMinutes(b.end);
    if (endMin > startMin) intervals.push({startMin, endMin});
  }
  return intervals;
};

/**
 * True when [startMin, endMin) overlaps any break interval (any-overlap rule).
 * Retained to mirror the backend rule module (which uses it for the Booked
 * Sessions out-of-rule flag); FE slot generation uses subtractBreaksFromWindows.
 */
export const slotOverlapsBreak = (
  startMin: number,
  endMin: number,
  breakIntervals: MinuteInterval[],
): boolean =>
  breakIntervals.some((iv) => startMin < iv.endMin && endMin > iv.startMin);

/** Remove the part of each segment in `segments` that falls inside break `b`. */
const carveSegmentsAroundBreak = (
  segments: MinuteInterval[],
  b: MinuteInterval,
): MinuteInterval[] => {
  const next: MinuteInterval[] = [];
  for (const seg of segments) {
    if (b.endMin <= seg.startMin || b.startMin >= seg.endMin) {
      next.push(seg); // break doesn't touch this segment
      continue;
    }
    if (seg.startMin < b.startMin) {
      next.push({startMin: seg.startMin, endMin: b.startMin});
    }
    if (b.endMin < seg.endMin) {
      next.push({startMin: b.endMin, endMin: seg.endMin});
    }
  }
  return next;
};

/**
 * Split booking windows around break intervals so slot generation resumes at
 * each break's end (rather than skipping to the next grid tick). Each input
 * window is carved by every break, yielding the remaining bookable sub-windows.
 * Returns the windows unchanged when there are no breaks.
 */
export const subtractBreaksFromWindows = (
  windows: MinuteInterval[],
  breakIntervals: MinuteInterval[],
): MinuteInterval[] => {
  if (!breakIntervals || breakIntervals.length === 0) return windows;
  const result: MinuteInterval[] = [];
  for (const w of windows) {
    let segments: MinuteInterval[] = [{startMin: w.startMin, endMin: w.endMin}];
    for (const b of breakIntervals) {
      segments = carveSegmentsAroundBreak(segments, b);
    }
    for (const seg of segments) {
      if (seg.endMin > seg.startMin) {
        result.push(
          w.alignToMin == null ? seg : {...seg, alignToMin: w.alignToMin},
        );
      }
    }
  }
  return result;
};

/** Convenience: day-of-week (0=Sun..6=Sat) for a 'YYYY-MM-DD' string. */
export const dayOfWeekForYmd = (ymd: string): number =>
  new Date(`${ymd}T00:00:00Z`).getUTCDay();

/**
 * Intersect availability windows with the session type's Time Range bounds
 * (OD-1002). Mirrors the backend intersectMinuteIntervals. Empty bounds mean no
 * Time Range constraint, so the windows are returned unchanged.
 */
export const intersectMinuteIntervals = (
  windows: MinuteInterval[],
  bounds: MinuteInterval[],
): MinuteInterval[] => {
  if (!Array.isArray(windows) || windows.length === 0) return [];
  if (!Array.isArray(bounds) || bounds.length === 0) return windows;
  const result: MinuteInterval[] = [];
  for (const w of windows) {
    for (const b of bounds) {
      const startMin = Math.max(w.startMin, b.startMin);
      const endMin = Math.min(w.endMin, b.endMin);
      if (endMin > startMin) {
        const alignToMin = b.alignToMin ?? w.alignToMin;
        result.push(
          alignToMin == null ? {startMin, endMin} : {startMin, endMin, alignToMin},
        );
      }
    }
  }
  return result;
};
